import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
  Divider,
  styled,
  Grid,
} from '@mui/material';
import { createSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CardActionArea from '@mui/material/CardActionArea';
import { getPercent } from '../helpers';
import { ConciliationStatus } from '../types';
import routes from '../config/routes';

type StatusCardProps = {
  title: string;
  countrySelected: string;
  data: ConciliationStatus[];
  loading: boolean;
};

type RowLinkProps = {
  to: { pathname: string; search: string };
  children: React.ReactNode;
};

const SmallTableCell = styled(TableCell)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const RowLink = ({ to, children }: RowLinkProps) => {
  const theme = useTheme();
  return (
    <TableRow
      component={Link}
      to={to}
      hover
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&&:hover': {
          backgroundColor: theme.palette.action.focus,
        },
      }}
    >
      {children}
    </TableRow>
  );
};

const ErrorStatusCell = () => {
  const { t } = useTranslation();
  return (
    <SmallTableCell component="th" scope="row">
      <Stack direction="row" alignItems="center" spacing={1}>
        <HighlightOffIcon color="error" />
        <Typography variant="body2" fontWeight={500}>
          {t('dashboard.conciliations-state.error')}
        </Typography>
      </Stack>
    </SmallTableCell>
  );
};

const OkStatusCell = () => {
  const { t } = useTranslation();
  return (
    <SmallTableCell component="th" scope="row">
      <Stack direction="row" alignItems="center" spacing={1}>
        <CheckCircleOutlineIcon color="success" />
        <Typography variant="body2" fontWeight={500}>
          {t('dashboard.conciliations-state.ok')}
        </Typography>
      </Stack>
    </SmallTableCell>
  );
};

const StatusCard = ({ countrySelected, title, data, loading }: StatusCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const totals = useMemo(() => {
    if (!data) return null;
    const totalClosuresReceived = data.reduce((acc, cur) => acc + (cur.closuresReceived || 0), 0);
    const totalOk = data.reduce((acc, cur) => acc + (cur.conciliationOk || 0), 0);
    const totalError = Math.max(totalClosuresReceived - totalOk, 0);

    return {
      closuresReceived: totalClosuresReceived,
      ok: totalOk,
      error: totalError,
      okPct: getPercent(totalOk, totalClosuresReceived),
      errorPct: getPercent(totalError, totalClosuresReceived),
      range:
        data.length > 0
          ? {
              startDate: data[0].startDate,
              endDate: data[0].endDate,
            }
          : undefined,
    };
  }, [data]);

  const makeTo = () => {
    const params: Record<string, string> = { country: countrySelected };
    if (totals?.range) {
      params.startDate = totals.range.startDate.toISOString();
      params.endDate = totals.range.endDate.toISOString();
    }
    return routes.protectedRoutes.conciliationHeader.path.concat(`?${createSearchParams(params)}`);
  };

  const gridProps = { xs: 12, sm: 12, md: 12, lg: 3 };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 2,
        background: theme.palette.background.paper,
      }}
    >
      <CardActionArea component={Link} to={makeTo()}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.primary" textAlign="center">
            {t(title)}
          </Typography>
          <Box
            sx={{
              height: 3,
              width: 160,
              borderRadius: ' 12px 12px 0 0',
              bgcolor: theme.palette.error.main,
              mx: 'auto',
              my: 0,
            }}
          />
          <Divider sx={{ my: 0 }} />

          <Stack direction="row" alignItems="baseline" spacing={1}>
            {loading ? (
              <Skeleton width={30} />
            ) : (
              <Typography variant="h4" fontWeight={700}>
                {totals?.closuresReceived}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.conciliations-state.restaurants')}
            </Typography>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SmallTableCell />
                  <SmallTableCell align="right">
                    {t('dashboard.conciliations-state.closures-received')}
                  </SmallTableCell>
                  <SmallTableCell align="right">
                    {t('dashboard.conciliations-state.conciliated-ok')}
                  </SmallTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <>
                    <TableRow>
                      <SmallTableCell>
                        <ErrorStatusCell />
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Skeleton width={40} />
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Skeleton width={30} />
                      </SmallTableCell>
                    </TableRow>
                    <TableRow>
                      <SmallTableCell>
                        <OkStatusCell />
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Skeleton width={30} />
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Skeleton width={40} />
                      </SmallTableCell>
                    </TableRow>
                  </>
                )}

                {!loading && totals && (
                  <>
                    <TableRow>
                      <ErrorStatusCell />
                      <SmallTableCell align="right">
                        <Typography color="text.secondary">{totals.error}</Typography>
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Typography color="text.secondary" fontWeight={500}>
                          {totals.errorPct}%
                        </Typography>
                      </SmallTableCell>
                    </TableRow>

                    <TableRow>
                      <OkStatusCell />
                      <SmallTableCell align="right">
                        <Typography color="text.secondary">{totals.ok}</Typography>
                      </SmallTableCell>
                      <SmallTableCell align="right">
                        <Typography color="text.secondary" fontWeight={500}>
                          {totals.okPct}%
                        </Typography>
                      </SmallTableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default StatusCard;
